import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { posts as allPosts } from "@/data/blogData";
import useSeo from "@/lib/useSeo";
import { organizationSchema, webSiteSchema } from "@/lib/schema";
import {
  Stethoscope, Baby, Activity, HeartPulse, Scissors, Smile, Sparkles,
  ShieldCheck, Users, TrendingUp, ArrowRight, CheckCircle2, Lock, Award,
} from "lucide-react";

const specialties = [
  { icon: Stethoscope, name: "General Dentistry" },
  { icon: Baby, name: "Pediatric Dentistry", note: "Pedo" },
  { icon: Activity, name: "Endodontics", note: "Endo" },
  { icon: HeartPulse, name: "Periodontics", note: "Perio" },
  { icon: Scissors, name: "Oral Surgery" },
  { icon: Smile, name: "Orthodontics", note: "Ortho" },
  { icon: Sparkles, name: "Prosthodontics" },
];

const pmsSystems = [
  "Archy", "CareStack", "Cloud9", "Curve Hero", "Dental Vision", "Denticon",
  "DentiMax", "Dentrix", "Dentrix Ascend", "Dolphin", "DSN", "Eaglesoft",
  "Fuse", "Open Dental", "Ortho Edge", "OrthoTrac", "SoftDent", "Tab32",
];

const marqueeItems = ["Insurance Verification", "Claim Submission", "AR Follow-Up & Payment Posting", "Denial Management", "Insurance & Patient Collections", "Reporting & Analytics"];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

export default function Home() {
  useSeo({
    title: "ClariDent RCM | Dental Revenue Cycle Management",
    description: "Dental revenue cycle management done right — billing, claims, denials, AR and insurance follow-up so your team can focus on patients. We clear the way. You care for smiles.",
    path: "/",
    image: "https://claridentrcm.com/home-hero.jpg",
    schemas: [organizationSchema(), webSiteSchema()],
  });
  const posts = allPosts;

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(14,143,143,0.10),transparent_55%)]" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-14 items-center relative">
          <motion.div className="lg:col-span-7" initial="hidden" animate="show" variants={fadeUp}>
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 mb-6" data-testid="hero-label">
              We Clear The Way. You Care For Smiles.
            </p>
            <h1 className="font-display text-5xl sm:text-6xl tracking-tighter font-black text-navy-900 leading-[1.05]" data-testid="hero-heading">
              Dental RCM That Turns <span className="text-teal-600">Claims Into Cash.</span>
            </h1>
            <p className="mt-7 text-base sm:text-lg leading-relaxed text-slate-600 max-w-xl" data-testid="hero-subtext">
              We handle billing, claims, denials, AR and insurance follow-up — so your dental team can focus on patients, not paperwork.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/book" data-testid="hero-audit-btn" className="rounded-full bg-teal-600 px-8 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-teal-700 hover:-translate-y-1 hover:shadow-xl">
                Get Your Free RCM Audit
              </Link>
              <Link to="/contact" data-testid="hero-pricing-btn" className="rounded-full border-2 border-navy-900 px-8 py-3.5 text-sm font-bold text-navy-900 transition-all duration-200 hover:bg-navy-50 hover:-translate-y-1">
                Talk to an RCM Specialist
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm font-semibold text-navy-900">
              <span className="flex items-center gap-2"><ShieldCheck size={17} className="text-teal-600" /> HIPAA-Ready Security</span>
              <span className="flex items-center gap-2"><CheckCircle2 size={17} className="text-teal-600" /> $0 Setup Fee</span>
              <span className="flex items-center gap-2"><TrendingUp size={17} className="text-teal-600" /> Rates from 1.80%</span>
            </div>
          </motion.div>
          <motion.div className="lg:col-span-5 relative" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } }}>
            <img
              src="/home-hero.jpg"
              alt="Bright modern dental clinic reception and treatment area"
              className="rounded-3xl w-full h-[420px] object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-slate-50 border-y border-slate-200 py-4" data-testid="services-marquee">
        <Marquee speed={25} gradient={false}>
          {marqueeItems.map((item) => (
            <span key={item} className="mx-8 text-sm font-bold tracking-[0.2em] uppercase text-navy-900 flex items-center gap-8">
              {item} <span className="text-teal-600">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* WHY PARTNER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <img
            src="/home-why.jpg"
            alt="Dentist reassuring a smiling patient in a modern dental practice"
            className="rounded-3xl w-full h-[460px] object-cover"
          />
        </motion.div>
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 mb-4">Why Partner With ClariDent RCM?</p>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-navy-900" data-testid="why-heading">
            Your revenue cycle partner, <span className="text-teal-600">not just a vendor.</span>
          </h2>
          <ul className="mt-9 space-y-4">
            {[
              "Expert team with deep dental RCM knowledge and proven revenue cycle processes",
              "HIPAA-ready security practices protecting patient data on every workflow",
              "Transparent communication and real-time reporting you can check anytime",
              "Proven processes that reduce AR days and speed up collections",
              "Scalable solutions for practices of all sizes, solo to multi-location",
              "Dedicated to your practice's success — we only grow when you do",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-slate-600">
                <CheckCircle2 size={20} className="text-teal-600 shrink-0 mt-0.5" /> {item}
              </li>
            ))}
          </ul>
          <div className="mt-9 grid grid-cols-3 gap-4">
            {[
              { icon: Lock, t: "HIPAA-Ready Security", d: "Patient data always protected" },
              { icon: ShieldCheck, t: "100% Data Security", d: "Advanced, secure processes" },
              { icon: Award, t: "Quality You Can Trust", d: "Accuracy and consistency" },
            ].map((b) => (
              <div key={b.t} className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-center">
                <b.icon size={22} className="text-teal-600 mx-auto mb-2" />
                <p className="text-xs font-bold text-navy-900 uppercase tracking-wide">{b.t}</p>
                <p className="text-xs text-slate-500 mt-1">{b.d}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* SPECIALTIES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="max-w-2xl mb-14">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 mb-4">Dentistry We Support</p>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-navy-900" data-testid="specialties-heading">
            Dental Specialties We Support
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
            From general dentistry to specialty practices, our RCM expertise supports the complete dental care spectrum.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {specialties.map((s, i) => (
            <motion.div
              key={s.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 4) * 0.07, duration: 0.5 }}
              className="rounded-2xl border border-slate-200 bg-white p-6 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              data-testid={`specialty-card-${i}`}
            >
              <div className="h-12 w-12 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4">
                <s.icon size={22} className="text-teal-600" />
              </div>
              <h3 className="font-display text-base sm:text-lg font-semibold text-navy-900 leading-snug">{s.name}</h3>
              {s.note && <p className="text-xs text-slate-400 mt-1">({s.note})</p>}
            </motion.div>
          ))}
        </div>
        <p className="mt-10 text-center text-sm sm:text-base text-slate-600 font-semibold" data-testid="specialties-note">
          Whatever your dental specialty, we help keep your revenue cycle moving.
        </p>
        <p className="mt-2 text-center text-xs text-slate-400">
          We support the billing and revenue cycle operations of these practice types — clinical care always stays with the practice.
        </p>
      </section>

      {/* TECHNOLOGY / PMS */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 mb-4">Practice Management Software We Work With</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-navy-900" data-testid="pms-heading">
              Technology & Practice Management Systems
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
              Already using a dental PMS? We can work within the systems your practice already relies on.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="pms-grid">
            {pmsSystems.map((name) => (
              <div
                key={name}
                className="rounded-full border border-slate-200 bg-white px-4 py-3 text-center text-sm font-semibold text-navy-900 hover:border-teal-600 hover:text-teal-600 transition-colors duration-200"
                data-testid={`pms-${name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {name}
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-sm sm:text-base text-slate-600 max-w-2xl mx-auto">
            We work within the systems your practice already uses, helping your team maintain a smoother and more connected revenue cycle.
          </p>
        </div>
      </section>



      {/* HOW IT WORKS */}
      <section className="bg-navy-950 py-24 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-400 mb-4">How Our RCM Process Works</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold" data-testid="how-heading">
              Three steps. No guesswork.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { n: "01", t: "Free RCM Audit", d: "We review your claims, denials, and AR aging and show you exactly where revenue is leaking — at no cost, no obligation." },
              { n: "02", t: "Seamless Onboarding", d: "We integrate with your existing practice management software, handle HIPAA-compliant data transfer, and align workflows with your front desk." },
              { n: "03", t: "Measurable Results", d: "Clean claims out within 24 hours, denials worked daily, and real-time reporting so you always know your numbers." },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.5 }}
                className="rounded-2xl border border-slate-800 bg-navy-900/60 p-8 hover:border-teal-600/50 transition-colors duration-300"
                data-testid={`how-step-${i}`}
              >
                <p className="font-display text-5xl font-black text-teal-500/40">{s.n}</p>
                <h3 className="font-display text-2xl font-semibold mt-5">{s.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING HOOK */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="rounded-3xl bg-navy-950 px-8 py-16 sm:px-16 relative overflow-hidden" data-testid="pricing-hook">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_right,rgba(14,143,143,0.28),transparent_60%)]" />
          <div className="relative grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-400 mb-4">Pricing</p>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-white" data-testid="pricing-teaser-heading">
                Big-company billing. <span className="text-teal-400">Startup-honest pricing.</span>
              </h2>
              <p className="mt-5 text-sm sm:text-base text-slate-400 leading-relaxed max-w-md">
                Dental RCM expertise without the enterprise overhead. One simple rate card — no setup fee, no hidden charges, no surprises.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link to="/pricing" data-testid="pricing-teaser-link" className="rounded-full bg-teal-600 px-8 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-teal-500 hover:-translate-y-1 hover:shadow-xl">
                  See Our Simple Rate Card
                </Link>
                <Link to="/book" data-testid="pricing-hook-audit-btn" className="rounded-full border-2 border-white/70 px-8 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-white/10 hover:-translate-y-1">
                  Get Your Free RCM Audit
                </Link>
              </div>
            </div>
            <div className="text-center lg:text-right">
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-400">Rates from</p>
              <p className="font-display text-8xl sm:text-9xl font-black text-white leading-none mt-2">1.80<span className="text-4xl sm:text-5xl text-teal-400">%</span></p>
              <p className="text-slate-400 text-sm mt-4">$0 setup fee · Flat $950/month under $45K · Free audit first</p>
              <Link to="/pricing#calculator" data-testid="pricing-hook-calc-link" className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-teal-400 hover:gap-3 transition-all duration-200">
                See your rate in seconds <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="rounded-3xl bg-navy-950 px-8 py-16 sm:px-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(14,143,143,0.25),transparent_65%)]" />
          <div className="relative">
            <Users size={36} className="text-teal-400 mx-auto mb-6" />
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-white" data-testid="cta-heading">
              Focus on your patients. <span className="text-teal-400">We'll focus on your revenue.</span>
            </h2>
            <p className="mt-5 text-slate-400 max-w-xl mx-auto text-sm sm:text-base">
              Book your free RCM audit today. No obligation. No commitment. Just insights that add value.
            </p>
            <Link to="/book" data-testid="cta-audit-btn" className="mt-9 inline-block rounded-full bg-teal-600 px-10 py-4 text-sm font-bold text-white transition-all duration-200 hover:bg-teal-500 hover:-translate-y-1 hover:shadow-2xl">
              Book Your Free Audit
            </Link>
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 mb-4">Resources</p>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-navy-900" data-testid="blog-preview-heading">
                From the ClariDent RCM Blog
              </h2>
            </div>
            <Link to="/resources" data-testid="view-all-resources-link" className="inline-flex items-center gap-2 text-sm font-bold text-teal-600 hover:gap-3 transition-all duration-200">
              View All Resources <ArrowRight size={17} />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.slice(0, 3).map((p, i) => (
              <motion.article
                key={p.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 3) * 0.1, duration: 0.5 }}
                className="rounded-2xl border border-slate-200 bg-white overflow-hidden group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
                data-testid={`home-blog-card-${p.slug}`}
              >
                <div className="h-48 overflow-hidden bg-slate-100">
                  <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-widest mb-3">
                    <span className="text-teal-600">{p.category}</span>
                    <span className="text-slate-400">{p.read_time}</span>
                  </div>
                  <h3 className="font-display text-xl font-semibold text-navy-900 leading-snug">{p.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{p.excerpt}</p>
                  <Link to={`/resources/${p.slug}`} data-testid={`home-blog-read-${p.slug}`} className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-teal-600 group-hover:gap-3 transition-all duration-200">
                    Read More <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>


    </main>
  );
}
